import type { Offer } from '@repo/contracts/marketplace/offers/types';
import type { AnalyticsSkuViewedProps } from '@repo/platform-analytics/AnalyticsSkuViewed';
import {
  SEGMENT_SKU_VIEWED_LEVEL,
  SEGMENT_SKU_VIEWED_PRODUCT_TYPE,
  SEGMENT_SKU_VIEWED_SOURCE,
} from '@repo/platform-analytics/lib/constants';
import { FinishType } from '@repo/platform-analytics/types';
import { env } from '@repo/platform-env';

export const getContentProps = (creditCardOffer: Offer) => {
  const {
    details: { applyLink, isActive, isMonetizing, reviewLink },
  } = creditCardOffer;

  const isReview = !isActive || !isMonetizing || !applyLink;
  const destinationURL = isReview ? reviewLink : applyLink;

  return {
    isReview,
    destinationURL,
  };
};

export const getAnalytics = (
  analytics: {
    sectionName: string;
    sectionIndex: number;
    elementPosition: number;
    topic?: string;
  },
  creditCardOffer: Offer,
  sectionCategory?: string,
  skuViewedPropsAdditionalAttributes?: Record<string, unknown>,
) => {
  const {
    name,
    details: { applyLink, isActive, isMonetizing, monetizable },
    institution: { id: institutionId },
    id: offerId,
    product: { id: productId },
  } = creditCardOffer;

  let isReview = false;
  if (!isActive || !isMonetizing || !applyLink) {
    isReview = true;
  }

  const marketplacePayload = {
    destination_url: getContentProps(creditCardOffer).destinationURL,
    marketplace_institution_id: institutionId,
    marketplace_offer_id: offerId,
    marketplace_product_id: productId,
    marketplace_product_type: 'credit_cards',
    monetizable,
    section_name: analytics.sectionName,
    section_position: analytics.elementPosition,
    element_position: analytics.elementPosition,
  };

  const ctaAnalytics = {
    type: 'PRODUCT_CARD',
    finishType: isReview ? FinishType.InternalContent : FinishType.ExternalApplication,
    hasPrequalified: false,
    productPosition: analytics.elementPosition,
    includeClickHeaderParams: true,
    monetizable: monetizable,
    entityId: productId,
    callingDeployableName: 'cardfinder-v2',
    callingDeployableVersion: env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || '1.0.0',
    marketplacePayload,
    section: {
      name: analytics.sectionName,
      position: analytics.sectionIndex,
      category: sectionCategory || undefined,
    },
  };

  const skuViewedProps: AnalyticsSkuViewedProps = {
    additionalAttributes: skuViewedPropsAdditionalAttributes,
    id: offerId,
    impressionSection: analytics.sectionName,
    impressionPosition: analytics.elementPosition,
    level: SEGMENT_SKU_VIEWED_LEVEL,
    monetizable,
    name,
    offerId,
    offerName: name,
    productType: SEGMENT_SKU_VIEWED_PRODUCT_TYPE,
    source: SEGMENT_SKU_VIEWED_SOURCE,
  };

  return { ctaAnalytics, skuViewedProps, marketplacePayload };
};
